/* ============================================================
   Cart Drawer — Eira Theme
   Handles open/close, AJAX cart ops, timer, shipping bar,
   delivery estimate, action tabs, and suggested product ATC.
   ============================================================ */

(function () {
  'use strict';

  /* ── Selectors ──────────────────────────────────────────── */
  var DRAWER_SEL     = '#CartDrawer';
  var BACKDROP_SEL   = '.bc-cart-drawer__backdrop';
  var PANEL_SEL      = '.bc-cart-drawer__panel';
  var CLOSE_SEL      = '[data-cart-drawer-close]';
  var TOGGLE_SEL     = '[data-cart-drawer-toggle]';
  var BODY_SEL       = '.bc-cart-drawer__body';
  var ITEMS_SEL      = '.bc-cart-drawer__items';
  var COUNT_BADGE    = '.bc-cart-drawer__count-badge';
  var TIMER_SEL      = '.bc-cart-drawer__timer';
  var SHIP_FILL_SEL  = '.bc-cart-drawer__shipping-fill';
  var SHIP_TEXT_SEL  = '.bc-cart-drawer__shipping-text-amount';
  var TOTAL_PRICE    = '.bc-cart-drawer__total-price';
  var SAVINGS_TEXT   = '.bc-cart-drawer__savings-text';
  var SAVINGS_ORIG   = '.bc-cart-drawer__savings-original';
  var HEADER_COUNT   = '.bc-header__cart-count';
  var TAB_SEL        = '.bc-cart-drawer__action-tab';
  var PANEL_PREFIX   = 'bc-cart-drawer__panel--';

  /* ── State ──────────────────────────────────────────────── */
  var timerInterval = null;
  var qtyDebounceTimers = {}; /* per-line debounce timers */

  /* ── Helpers ────────────────────────────────────────────── */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    var moneyFormat = window.theme && window.theme.moneyFormat
      ? window.theme.moneyFormat
      : (window.Shopify && window.Shopify.money_format
        ? window.Shopify.money_format
        : '${{amount}}');

    return applyMoneyFormat(cents, moneyFormat);
  }

  function formatMoneyWithCurrency(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    var moneyFormat = window.theme && window.theme.moneyWithCurrencyFormat
      ? window.theme.moneyWithCurrencyFormat
      : (window.Shopify && window.Shopify.money_with_currency_format
        ? window.Shopify.money_with_currency_format
        : '${{amount}} USD');

    return applyMoneyFormat(cents, moneyFormat);
  }

  function applyMoneyFormat(cents, moneyFormat) {
    var value = cents / 100;

    function addThousandsSep(numStr, sep) {
      var parts = numStr.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return parts.join('.');
    }

    var amount = addThousandsSep(value.toFixed(2), ',');
    var amountNoDecimals = addThousandsSep(Math.round(value).toString(), ',');
    var amountWithComma = addThousandsSep(value.toFixed(2), '.').replace(/\.(\d{2})$/, ',$1');
    var amountNoDecimalsWithComma = addThousandsSep(Math.round(value).toString(), '.');
    var amountWithApostrophe = addThousandsSep(value.toFixed(2), "'");
    var amountWithSpace = addThousandsSep(value.toFixed(2), ' ');
    var amountNoDecimalsWithSpace = addThousandsSep(Math.round(value).toString(), ' ');

    return moneyFormat
      .replace(/\{\{amount_with_comma_separator\}\}/g, amountWithComma)
      .replace(/\{\{amount_no_decimals_with_comma_separator\}\}/g, amountNoDecimalsWithComma)
      .replace(/\{\{amount_with_apostrophe_separator\}\}/g, amountWithApostrophe)
      .replace(/\{\{amount_no_decimals_with_space_separator\}\}/g, amountNoDecimalsWithSpace)
      .replace(/\{\{amount_with_space_separator\}\}/g, amountWithSpace)
      .replace(/\{\{amount_no_decimals\}\}/g, amountNoDecimals)
      .replace(/\{\{amount\}\}/g, amount);
  }

  /**
   * Convert a Shopify CDN image URL to a sized variant.
   * Handles both legacy (_200x.) and modern (?width=200) URL formats.
   */
  function getSizedImageUrl(url, size) {
    if (!url) return '';
    /* Remove any protocol-relative prefix for consistency */
    var src = url.replace(/^https?:/, '');
    /* If the URL already has crop/width params from Shopify files API, use the width param approach */
    if (src.indexOf('/files/') !== -1) {
      /* Modern Shopify file URLs: append or replace width param */
      var base = src.split('?')[0];
      return base + '?width=' + size + '&crop=center';
    }
    /* Legacy product image URLs: insert _SIZEx before the extension */
    var match = src.match(/(\.[^.]+)$/);
    if (match) {
      return src.replace(match[1], '_' + size + 'x' + match[1]);
    }
    return src;
  }

  function getDrawer() {
    return document.querySelector(DRAWER_SEL);
  }

  /* ── Open / Close ───────────────────────────────────────── */
  function openDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.classList.add('bc-cart-drawer-open');
    startTimer(drawer);
    /* Trap focus inside drawer */
    var panel = drawer.querySelector(PANEL_SEL);
    if (panel) {
      var firstFocusable = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) setTimeout(function() { firstFocusable.focus(); }, 100);
    }
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.classList.remove('bc-cart-drawer-open');
    /* Close any open action panels */
    var panels = drawer.querySelectorAll('.bc-cart-drawer__action-panel');
    panels.forEach(function(p) { p.classList.remove('is-open'); });
    var tabs = drawer.querySelectorAll(TAB_SEL);
    tabs.forEach(function(t) { t.classList.remove('is-active'); });
  }

  /* ── Timer ──────────────────────────────────────────────── */
  function startTimer(drawer) {
    var timerEl = drawer.querySelector(TIMER_SEL);
    if (!timerEl) return;
    var minutes = parseInt(timerEl.dataset.timerMinutes, 10) || 10;

    /* Check if we have a stored end time */
    var storageKey = 'bc_cart_timer_end';
    var endTime = sessionStorage.getItem(storageKey);
    var now = Date.now();

    if (!endTime || parseInt(endTime, 10) <= now) {
      endTime = now + minutes * 60 * 1000;
      sessionStorage.setItem(storageKey, endTime);
    } else {
      endTime = parseInt(endTime, 10);
    }

    if (timerInterval) clearInterval(timerInterval);

    function updateTimer() {
      var remaining = Math.max(0, endTime - Date.now());
      var mins = Math.floor(remaining / 60000);
      var secs = Math.floor((remaining % 60000) / 1000);
      var display = (mins < 10 ? '0' : '') + mins + ':' + (secs < 10 ? '0' : '') + secs;
      var bold = timerEl.querySelector('.bc-cart-drawer__timer-bold');
      if (bold) bold.textContent = display;
      if (remaining <= 0 && timerInterval) {
        clearInterval(timerInterval);
      }
    }

    updateTimer();
    timerInterval = setInterval(updateTimer, 1000);
  }

  /* ── Shipping Bar ───────────────────────────────────────── */
  function updateShippingBar(cartTotal) {
    var drawer = getDrawer();
    if (!drawer) return;
    var fill = drawer.querySelector(SHIP_FILL_SEL);
    var textEl = drawer.querySelector(SHIP_TEXT_SEL);
    var barWrap = drawer.querySelector('.bc-cart-drawer__shipping-bar');
    if (!barWrap) return;

    var rawThreshold = barWrap.dataset.threshold;
    var threshold = (rawThreshold !== undefined && rawThreshold !== '')
      ? parseInt(rawThreshold, 10)
      : 10000; /* cents fallback only if attribute missing entirely */

    /* If threshold is NaN (corrupt value), fall back to 10000 */
    if (isNaN(threshold)) threshold = 10000;

    var successText = barWrap.dataset.successText || "You've unlocked FREE shipping!";

    /* Threshold of 0 means free shipping is always unlocked */
    if (threshold === 0) {
      if (fill) fill.style.width = '100%';
      if (textEl) {
        textEl.innerHTML = '<span class="bc-cart-drawer__shipping-success">' + successText + '</span>';
      }
      return;
    }

    if (fill) {
      var pct = Math.min(100, (cartTotal / threshold) * 100);
      fill.style.width = pct + '%';
    }

    if (textEl) {
      if (cartTotal >= threshold) {
        textEl.innerHTML = '<span class="bc-cart-drawer__shipping-success">' + successText + '</span>';
      } else {
        var remaining = threshold - cartTotal;
        textEl.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more to get FREE shipping';
      }
    }
  }

  /* ── Error Toast ─────────────────────────────────────────── */
  var errorToastTimeout = null;

  function showErrorToast(message) {
    var drawer = getDrawer();
    if (!drawer) return;
    var toast = drawer.querySelector('.bc-cart-drawer__error-toast');
    if (!toast) {
      /* Create error toast if it doesn't exist in DOM */
      toast = document.createElement('div');
      toast.className = 'bc-cart-drawer__error-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.setAttribute('aria-atomic', 'true');
      toast.innerHTML = '<span class="bc-cart-drawer__error-text"></span><button type="button" class="bc-cart-drawer__error-dismiss" aria-label="Dismiss">&times;</button>';
      drawer.querySelector(PANEL_SEL).appendChild(toast);
      toast.querySelector('.bc-cart-drawer__error-dismiss').addEventListener('click', function() {
        toast.classList.remove('is-visible');
      });
    }
    toast.querySelector('.bc-cart-drawer__error-text').textContent = message;
    toast.classList.add('is-visible');

    if (errorToastTimeout) clearTimeout(errorToastTimeout);
    errorToastTimeout = setTimeout(function() {
      toast.classList.remove('is-visible');
    }, 6000);
  }

  function clearItemLoadingStates() {
    var drawer = getDrawer();
    if (!drawer) return;
    drawer.querySelectorAll('.bc-cart-drawer__item.is-loading').forEach(function(el) {
      el.classList.remove('is-loading');
    });
  }

  /* ── AJAX Cart Change ───────────────────────────────────── */
  function changeCartItem(line, quantity, callback) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
    .then(function(res) {
      if (!res.ok) {
        return res.json().then(function(data) {
          throw new Error(data.description || data.message || 'Could not update cart');
        });
      }
      return res.json();
    })
    .then(function(cart) {
      updateDrawerFromCart(cart);
      if (callback) callback(cart);
    })
    .catch(function(err) {
      console.error('Cart change error:', err);
      clearItemLoadingStates();
      showErrorToast(err.message || 'Something went wrong. Please try again.');
    });
  }

  /* Promise version for chaining */
  function changeCartItemPromise(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
    .then(function(res) {
      if (!res.ok) {
        return res.json().then(function(data) {
          throw new Error(data.description || data.message || 'Could not update cart');
        });
      }
      return res.json();
    });
  }

  /* Debounced quantity change — waits 300ms after last click */
  function debouncedChangeCartItem(line, quantity) {
    if (qtyDebounceTimers[line]) clearTimeout(qtyDebounceTimers[line]);
    qtyDebounceTimers[line] = setTimeout(function() {
      delete qtyDebounceTimers[line];
      changeCartItem(line, quantity);
    }, 300);
  }

  function addToCart(variantId, quantity, callback) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    })
    .then(function(res) {
      if (!res.ok) {
        return res.json().then(function(data) {
          throw new Error(data.description || data.message || 'Could not add item to cart');
        });
      }
      return res.json();
    })
    .then(function() {
      /* Fetch full cart after add */
      return fetch('/cart.js').then(function(r) { return r.json(); });
    })
    .then(function(cart) {
      updateDrawerFromCart(cart);
      openDrawer();
      if (callback) callback(cart);
    })
    .catch(function(err) {
      console.error('Cart add error:', err);
      showErrorToast(err.message || 'Could not add to cart. Please try again.');
      if (callback) callback(null, err);
    });
  }

  /* ── Update Note ────────────────────────────────────────── */
  function updateCartNote(note) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note })
    })
    .catch(function(err) {
      console.error('Cart note error:', err);
    });
  }

  /* ── Refresh drawer from /cart.js ───────────────────────── */
  function updateDrawerFromCart(cart) {
    var drawer = getDrawer();
    if (!drawer) return;

    /* Update count badges */
    var badges = drawer.querySelectorAll(COUNT_BADGE);
    badges.forEach(function(b) { b.textContent = cart.item_count; });

    /* Update header count */
    var headerCounts = document.querySelectorAll(HEADER_COUNT);
    headerCounts.forEach(function(el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });

    /* Update totals */
    var totalEls = drawer.querySelectorAll(TOTAL_PRICE);
    totalEls.forEach(function(el) {
      el.textContent = formatMoneyWithCurrency(cart.total_price);
    });

    /* Update savings */
    var totalSavings = cart.original_total_price - cart.total_price;
    var savingsEls = drawer.querySelectorAll(SAVINGS_TEXT);
    savingsEls.forEach(function(el) {
      if (totalSavings > 0) {
        el.textContent = "You've saved " + formatMoney(totalSavings) + "!";
        el.closest('.bc-cart-drawer__savings-row').style.display = '';
      } else {
        el.closest('.bc-cart-drawer__savings-row').style.display = 'none';
      }
    });

    var origEls = drawer.querySelectorAll(SAVINGS_ORIG);
    origEls.forEach(function(el) {
      el.textContent = formatMoney(cart.original_total_price);
    });

    /* Update shipping bar */
    updateShippingBar(cart.total_price);

    /* Rebuild items */
    rebuildItems(cart, drawer);

    /* Sync gift wrap UI */
    syncGiftWrapUI(cart);

    /* If cart is empty, show empty state */
    var body = drawer.querySelector(BODY_SEL);
    var emptyState = drawer.querySelector('.bc-cart-drawer__empty');
    var footer = drawer.querySelector('.bc-cart-drawer__footer');
    var actionsBar = drawer.querySelector('.bc-cart-drawer__actions-bar');
    var timerEl = drawer.querySelector(TIMER_SEL);
    var shipBar = drawer.querySelector('.bc-cart-drawer__shipping-bar');
    var deliveryEl = drawer.querySelector('.bc-cart-drawer__delivery');

    if (cart.item_count === 0) {
      if (body) body.style.display = 'none';
      if (emptyState) emptyState.style.display = '';
      if (footer) footer.style.display = 'none';
      if (actionsBar) actionsBar.style.display = 'none';
      if (timerEl) timerEl.style.display = 'none';
      if (shipBar) shipBar.style.display = 'none';
      if (deliveryEl) deliveryEl.style.display = 'none';
    } else {
      if (body) body.style.display = '';
      if (emptyState) emptyState.style.display = 'none';
      if (footer) footer.style.display = '';
      if (actionsBar) actionsBar.style.display = '';
      if (timerEl) timerEl.style.display = '';
      if (shipBar) shipBar.style.display = '';
      if (deliveryEl) deliveryEl.style.display = '';
    }
  }

  /* ── Rebuild item list DOM ──────────────────────────────── */
  function rebuildItems(cart, drawer) {
    var container = drawer.querySelector(ITEMS_SEL);
    if (!container) return;

    /* Read delivery settings from the delivery element */
    var deliveryEl = drawer.querySelector('.bc-cart-drawer__delivery');
    var minDays = deliveryEl ? parseInt(deliveryEl.dataset.minDays, 10) || 3 : 3;
    var maxDays = deliveryEl ? parseInt(deliveryEl.dataset.maxDays, 10) || 5 : 5;

    var html = '';

    cart.items.forEach(function(item, index) {
      var line = index + 1;
      var hasCompare = item.original_price > item.final_price;
      var imgSrc = getSizedImageUrl(item.image, 200);

      /* Check if this is the gift-wrap product */
      var giftWrapEl = document.querySelector('[data-gift-wrap]');
      var isGiftWrap = giftWrapEl && item.product_id === parseInt(giftWrapEl.dataset.giftWrapProductId, 10);

      /* Skip rendering gift wrap as a normal cart item — it's controlled by the toggle */
      if (isGiftWrap) return;

      /* Build variant text */
      var variantText = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantText = item.variant_title;
      }

      /* Build delivery note for each item */
      var now = new Date();
      var minDate = new Date(now);
      minDate.setDate(minDate.getDate() + minDays);
      var maxDate = new Date(now);
      maxDate.setDate(maxDate.getDate() + maxDays);
      var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      var delivNote = 'Estimated delivery between ' + months[minDate.getMonth()] + ' ' + (minDate.getDate() < 10 ? '0' : '') + minDate.getDate()
        + ' and ' + months[maxDate.getMonth()] + ' ' + (maxDate.getDate() < 10 ? '0' : '') + maxDate.getDate() + '.';

      /* Order urgency */
      var endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      var minsLeft = Math.floor((endOfDay - now) / 60000);
      var urgency = '';
      if (minsLeft > 60) {
        urgency = ' Order within <strong>' + Math.floor(minsLeft / 60) + ' hours</strong>.';
      } else {
        urgency = ' Order within <strong>' + minsLeft + ' minutes</strong>.';
      }

      html += '<div class="bc-cart-drawer__item" data-line="' + line + '">';
      html += '  <a href="' + item.url + '" class="bc-cart-drawer__item-image">';
      if (imgSrc) {
        var imgSrc2x = getSizedImageUrl(item.image, 400);
        html += '    <img src="' + imgSrc + '" srcset="' + imgSrc + ' 1x, ' + imgSrc2x + ' 2x" alt="' + (item.title || '').replace(/"/g, '&quot;') + '" loading="lazy" width="200" height="250">';
      }
      html += '  </a>';
      html += '  <div class="bc-cart-drawer__item-details">';
      html += '    <div class="bc-cart-drawer__item-header">';
      html += '      <a href="' + item.url + '" class="bc-cart-drawer__item-title">' + item.product_title + '</a>';
      html += '      <div class="bc-cart-drawer__item-prices">';
      if (hasCompare) {
        html += '        <span class="bc-cart-drawer__item-compare">' + formatMoney(item.original_price) + '</span>';
      }
      html += '        <span class="bc-cart-drawer__item-price">' + formatMoney(item.final_price) + '</span>';
      html += '      </div>';
      html += '    </div>';
      if (variantText) {
        html += '    <p class="bc-cart-drawer__item-variant">' + variantText + '</p>';
      }
      html += '    <p class="bc-cart-drawer__item-note">Note: ' + delivNote + urgency + '</p>';
      html += '    <div class="bc-cart-drawer__item-actions">';
      html += '      <div class="bc-cart-drawer__qty">';
      html += '        <button type="button" class="bc-cart-drawer__qty-btn" data-drawer-qty-minus data-line="' + line + '"' + (item.quantity <= 1 ? ' disabled' : '') + '>−</button>';
      html += '        <input type="number" class="bc-cart-drawer__qty-input" value="' + item.quantity + '" min="0" data-drawer-qty-input data-line="' + line + '" aria-label="Quantity">';
      html += '        <button type="button" class="bc-cart-drawer__qty-btn" data-drawer-qty-plus data-line="' + line + '">+</button>';
      html += '      </div>';
      html += '      <button type="button" class="bc-cart-drawer__item-remove" data-drawer-remove data-line="' + line + '">Remove</button>';
      html += '    </div>';

      /* Line-level discounts */
      if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
        item.line_level_discount_allocations.forEach(function(disc) {
          html += '    <div class="bc-cart-drawer__item-discount">';
          html += '      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
          html += '      ' + disc.discount_application.title;
          html += '    </div>';
        });
      }

      html += '  </div>';
      html += '</div>';
    });

    container.innerHTML = html;
  }

  /* ── Undo Toast ───────────────────────────────────────────── */
  var undoTimeout = null;
  var lastRemovedVariantId = null;
  var lastRemovedQty = null;

  function showUndoToast() {
    var toast = document.getElementById('CartDrawerUndoToast');
    if (!toast) return;

    /* Show the toast */
    toast.classList.add('is-visible');

    /* Auto-hide after 5 seconds */
    if (undoTimeout) clearTimeout(undoTimeout);
    undoTimeout = setTimeout(function() {
      toast.classList.remove('is-visible');
      lastRemovedVariantId = null;
      lastRemovedQty = null;
    }, 5000);
  }

  /* ── Gift Wrap Logic ───────────────────────────────────────── */
  function getGiftWrapConfig() {
    var el = document.querySelector('[data-gift-wrap]');
    if (!el) return null;
    return {
      variantId: parseInt(el.dataset.giftWrapVariantId, 10),
      productId: parseInt(el.dataset.giftWrapProductId, 10),
      inCart: el.dataset.giftWrapInCart === 'true',
      line: parseInt(el.dataset.giftWrapLine, 10) || 0
    };
  }

  function setGiftWrapLoading(loading) {
    var els = document.querySelectorAll('[data-gift-wrap]');
    els.forEach(function(el) {
      if (loading) {
        el.classList.add('is-loading');
      } else {
        el.classList.remove('is-loading');
      }
    });
  }

  function syncGiftWrapUI(cart) {
    var config = getGiftWrapConfig();
    if (!config) return;

    var found = false;
    var line = 0;

    /* Count real (non-gift-wrap) items to decide visibility */
    var realItemCount = 0;

    cart.items.forEach(function(item, idx) {
      if (item.product_id === config.productId) {
        found = true;
        line = idx + 1;
      } else {
        realItemCount++;
      }
    });

    /* Update all gift-wrap toggles on the page */
    document.querySelectorAll('[data-gift-wrap]').forEach(function(el) {
      el.dataset.giftWrapInCart = found ? 'true' : 'false';
      el.dataset.giftWrapLine = line;

      /* ── KEY FIX: hide toggle when cart has no real items ── */
      el.style.display = realItemCount === 0 ? 'none' : '';

      var checkbox = el.querySelector('[data-gift-wrap-checkbox]');
      if (checkbox) checkbox.checked = found;

      var msgWrap = el.querySelector('[data-gift-wrap-message-wrap]');
      if (msgWrap) {
        if (found) {
          msgWrap.removeAttribute('hidden');
        } else {
          msgWrap.setAttribute('hidden', '');
        }
      }
    });
  }

  function addGiftWrapToCart(variantId, message) {
    var properties = {};
    if (message && message.trim()) {
      properties['Gift Message'] = message.trim();
    }

    var body = {
      items: [{
        id: variantId,
        quantity: 1,
        properties: Object.keys(properties).length > 0 ? properties : undefined
      }]
    };

    setGiftWrapLoading(true);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Could not add gift wrapping');
      return fetch('/cart.js');
    })
    .then(function(res) { return res.json(); })
    .then(function(cart) {
      setGiftWrapLoading(false);
      syncGiftWrapUI(cart);
      return cart;
    })
    .catch(function(err) {
      setGiftWrapLoading(false);
      showErrorToast('Could not add gift wrapping. Please try again.');
      throw err;
    });
  }

  function removeGiftWrapFromCart(variantId) {
    setGiftWrapLoading(true);

    /* Use variant id (as string) — Shopify /cart/change.js accepts this to target the item */
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 0 })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Could not remove gift wrapping');
      return res.json();
    })
    .then(function(cart) {
      setGiftWrapLoading(false);
      syncGiftWrapUI(cart);
      return cart;
    })
    .catch(function(err) {
      setGiftWrapLoading(false);
      showErrorToast('Could not remove gift wrapping. Please try again.');
      throw err;
    });
  }

  /**
   * Update the gift message without remove+add race condition.
   * Uses /cart/change.js with the variant id and new properties.
   */
  function updateGiftWrapMessage(variantId, message) {
    setGiftWrapLoading(true);

    var properties = {};
    if (message && message.trim()) {
      properties['Gift Message'] = message.trim();
    }

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: properties })
    })
    .then(function(res) {
      if (!res.ok) throw new Error('Could not update gift message');
      return res.json();
    })
    .then(function(cart) {
      setGiftWrapLoading(false);
      syncGiftWrapUI(cart);
      return cart;
    })
    .catch(function(err) {
      setGiftWrapLoading(false);
      console.error('Gift message update error:', err);
      throw err;
    });
  }

  var giftWrapInitialized = false;

  function initGiftWrap() {
    if (giftWrapInitialized) return;
    giftWrapInitialized = true;

    document.addEventListener('change', function(e) {
      var checkbox = e.target.closest('[data-gift-wrap-checkbox]');
      if (!checkbox) return;

      var wrap = checkbox.closest('[data-gift-wrap]');
      if (!wrap) return;

      /* Prevent double-fire while a request is in progress */
      if (wrap.classList.contains('is-loading')) return;

      var variantId = parseInt(wrap.dataset.giftWrapVariantId, 10);

      if (checkbox.checked) {
        /* Add gift wrap */
        var msgField = wrap.querySelector('[data-gift-wrap-message]');
        var message = msgField ? msgField.value : '';
        addGiftWrapToCart(variantId, message).then(function(cart) {
          updateDrawerFromCart(cart);
        }).catch(function() {
          checkbox.checked = false;
        });
      } else {
        /* Remove gift wrap */
        removeGiftWrapFromCart(variantId).then(function(cart) {
          updateDrawerFromCart(cart);
        }).catch(function() {
          checkbox.checked = true;
        });
      }
    });

    /* Debounced gift message update — uses in-place property change (no remove+add) */
    var giftMsgTimer = null;
    document.addEventListener('input', function(e) {
      var msgField = e.target.closest('[data-gift-wrap-message]');
      if (!msgField) return;

      var wrap = msgField.closest('[data-gift-wrap]');
      if (!wrap || wrap.dataset.giftWrapInCart !== 'true') return;

      var variantId = parseInt(wrap.dataset.giftWrapVariantId, 10);

      if (giftMsgTimer) clearTimeout(giftMsgTimer);
      giftMsgTimer = setTimeout(function() {
        updateGiftWrapMessage(variantId, msgField.value).then(function(cart) {
          updateDrawerFromCart(cart);
        });
      }, 800);
    });
  }

  /* ── Event Delegation ───────────────────────────────────── */
  function init() {
    /* Toggle buttons (header cart icon) */
    document.addEventListener('click', function(e) {
      var toggle = e.target.closest(TOGGLE_SEL);
      if (toggle) {
        e.preventDefault();
        var drawer = getDrawer();
        if (drawer && drawer.classList.contains('is-open')) {
          closeDrawer();
        } else {
          openDrawer();
        }
        return;
      }

      /* Close button */
      if (e.target.closest(CLOSE_SEL)) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      /* Backdrop click */
      if (e.target.closest(BACKDROP_SEL)) {
        closeDrawer();
        return;
      }

      /* Quantity minus */
      var minusBtn = e.target.closest('[data-drawer-qty-minus]');
      if (minusBtn) {
        var line = parseInt(minusBtn.dataset.line, 10);
        var input = minusBtn.parentElement.querySelector('[data-drawer-qty-input]');
        var current = parseInt(input.value, 10) || 1;
        var next = Math.max(0, current - 1);
        input.value = next;
        minusBtn.disabled = next <= 1;
        var itemEl = minusBtn.closest('.bc-cart-drawer__item');
        if (itemEl) itemEl.classList.add('is-loading');
        debouncedChangeCartItem(line, next);
        return;
      }

      /* Quantity plus */
      var plusBtn = e.target.closest('[data-drawer-qty-plus]');
      if (plusBtn) {
        var line2 = parseInt(plusBtn.dataset.line, 10);
        var input2 = plusBtn.parentElement.querySelector('[data-drawer-qty-input]');
        var current2 = parseInt(input2.value, 10) || 1;
        var next2 = current2 + 1;
        input2.value = next2;
        var minBtn = plusBtn.parentElement.querySelector('[data-drawer-qty-minus]');
        if (minBtn) minBtn.disabled = false;
        var itemEl2 = plusBtn.closest('.bc-cart-drawer__item');
        if (itemEl2) itemEl2.classList.add('is-loading');
        debouncedChangeCartItem(line2, next2);
        return;
      }

      /* Remove */
      var removeBtn = e.target.closest('[data-drawer-remove]');
      if (removeBtn) {
        e.preventDefault();
        var removeLine = parseInt(removeBtn.dataset.line, 10);
        var itemRow = removeBtn.closest('.bc-cart-drawer__item');

        /* Capture item info for undo before removing */
        var qtyInput = itemRow ? itemRow.querySelector('[data-drawer-qty-input]') : null;
        var capturedQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        /* Show loading state */
        if (itemRow) itemRow.classList.add('is-loading');

        /* We need the variant ID — fetch cart first to get it */
        fetch('/cart.js')
          .then(function(r) { return r.json(); })
          .then(function(cart) {
            var item = cart.items[removeLine - 1];
            if (item) {
              lastRemovedVariantId = item.variant_id;
              lastRemovedQty = capturedQty;
            }
            return changeCartItemPromise(removeLine, 0);
          })
          .then(function(cart) {
            updateDrawerFromCart(cart);
            showUndoToast();
          })
          .catch(function() {
            if (itemRow) itemRow.classList.remove('is-loading');
          });
        return;
      }

      /* Action tabs */
      var tab = e.target.closest(TAB_SEL);
      if (tab && !tab.hasAttribute('data-cart-share')) {
        var drawer3 = getDrawer();
        if (!drawer3) return;
        var targetPanel = tab.dataset.tabTarget;
        if (!targetPanel) return;

        var panel = drawer3.querySelector('#' + targetPanel);
        var isOpen = panel && panel.classList.contains('is-open');

        /* Close all panels and deactivate tabs */
        drawer3.querySelectorAll('.bc-cart-drawer__action-panel').forEach(function(p) {
          p.classList.remove('is-open');
          p.setAttribute('aria-hidden', 'true');
        });
        drawer3.querySelectorAll(TAB_SEL + '[role="tab"]').forEach(function(t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });

        /* Toggle the clicked one */
        if (!isOpen && panel) {
          panel.classList.add('is-open');
          panel.setAttribute('aria-hidden', 'false');
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
        }
        return;
      }

      /* Share cart */
      var shareBtn = e.target.closest('[data-cart-share]');
      if (shareBtn) {
        e.preventDefault();
        var url = window.location.origin + '/cart';

        if (navigator.share) {
          navigator.share({ title: 'My Cart', url: url }).catch(function() {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function() {
            var origHTML = shareBtn.innerHTML;
            shareBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
            setTimeout(function() { shareBtn.innerHTML = origHTML; }, 2000);
          }).catch(function() {
            /* Fallback: select from hidden input */
            var tempInput = document.createElement('input');
            tempInput.value = url;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            var origHTML = shareBtn.innerHTML;
            shareBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><polyline points="20 6 9 17 4 12"/></svg> Copied!';
            setTimeout(function() { shareBtn.innerHTML = origHTML; }, 2000);
          });
        }
        return;
      }

      /* Undo removal */
      var undoBtn = e.target.closest('[data-cart-undo]');
      if (undoBtn) {
        e.preventDefault();
        var toast = document.getElementById('CartDrawerUndoToast');
        if (toast) toast.classList.remove('is-visible');
        if (undoTimeout) clearTimeout(undoTimeout);
        /* Re-add the removed item */
        if (lastRemovedVariantId && lastRemovedQty) {
          addToCart(lastRemovedVariantId, lastRemovedQty);
          lastRemovedVariantId = null;
          lastRemovedQty = null;
        }
        return;
      }

      /* Suggestion ATC */
      var suggestionAtc = e.target.closest('[data-suggestion-atc]');
      if (suggestionAtc) {
        e.preventDefault();
        var variantId = parseInt(suggestionAtc.dataset.variantId, 10);
        if (!variantId) return;
        var origHtml = suggestionAtc.innerHTML;
        suggestionAtc.disabled = true;
        suggestionAtc.textContent = 'Adding…';
        addToCart(variantId, 1, function() {
          suggestionAtc.innerHTML = '✓ Added';
          setTimeout(function() {
            suggestionAtc.innerHTML = origHtml;
            suggestionAtc.disabled = false;
          }, 1500);
        });
        return;
      }

      /* FBT navigation arrows */
      var fbtPrev = e.target.closest('[data-fbt-prev]');
      if (fbtPrev) {
        var track = fbtPrev.closest('.bc-fbt').querySelector('[data-fbt-track]');
        if (track) track.scrollBy({ left: -142, behavior: 'smooth' });
        return;
      }

      var fbtNext = e.target.closest('[data-fbt-next]');
      if (fbtNext) {
        var track2 = fbtNext.closest('.bc-fbt').querySelector('[data-fbt-track]');
        if (track2) track2.scrollBy({ left: 142, behavior: 'smooth' });
        return;
      }
    });

    /* Quantity input change */
    document.addEventListener('change', function(e) {
      var input = e.target.closest('[data-drawer-qty-input]');
      if (!input) return;
      var line = parseInt(input.dataset.line, 10);
      var val = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = val;
      debouncedChangeCartItem(line, val);
    });

    /* Order note debounce */
    var noteDebounce = null;
    document.addEventListener('input', function(e) {
      var noteField = e.target.closest('[data-cart-drawer-note]');
      if (!noteField) return;
      if (noteDebounce) clearTimeout(noteDebounce);
      noteDebounce = setTimeout(function() {
        updateCartNote(noteField.value);
      }, 500);
    });

    /* Escape key */
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeDrawer();
    });

    /* Intercept all ATC forms sitewide when drawer mode is on */
    document.addEventListener('submit', function(e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      var drawer = getDrawer();
      if (!drawer) return; /* drawer not in DOM = page mode */

      e.preventDefault();
      var formData = new FormData(form);
      var id = formData.get('id');
      var qty = parseInt(formData.get('quantity'), 10) || 1;
      if (!id) return;

      /* Visual feedback on submit button */
      var submitBtn = form.querySelector('button[type="submit"]');
      var origContent = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding…';
      }

      addToCart(parseInt(id, 10), qty, function(cart, err) {
        if (err) {
          /* Reset button on failure */
          if (submitBtn) {
            submitBtn.innerHTML = origContent;
            submitBtn.disabled = false;
          }
          return;
        }
        if (submitBtn) {
          submitBtn.innerHTML = '✓ Added!';
          setTimeout(function() {
            submitBtn.innerHTML = origContent;
            submitBtn.disabled = false;
          }, 1500);
        }
      });
    });

    /* Initial shipping bar update — fetch real cart to avoid stale cached totals */
    var drawer = getDrawer();
    if (drawer) {
      var initTotal = parseInt(drawer.dataset.cartTotal, 10) || 0;
      updateShippingBar(initTotal);

      /* Fetch live cart data to correct any stale server-rendered values */
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          if (cart.total_price !== initTotal) {
            updateShippingBar(cart.total_price);
            /* Also sync the count badge and totals */
            var badges = drawer.querySelectorAll(COUNT_BADGE);
            badges.forEach(function(b) { b.textContent = cart.item_count; });
            var headerCounts = document.querySelectorAll(HEADER_COUNT);
            headerCounts.forEach(function(el) {
              el.textContent = cart.item_count;
              el.style.display = cart.item_count > 0 ? '' : 'none';
            });
            var totalEls = drawer.querySelectorAll(TOTAL_PRICE);
            totalEls.forEach(function(el) {
              el.textContent = formatMoneyWithCurrency(cart.total_price);
            });
          }
        })
        .catch(function() { /* silent — initial render is still OK */ });
    }

    /* Initialize gift wrap handlers */
    initGiftWrap();
  }

  /* ── Boot ────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Listen for custom event from quick-view (and any other module) */
  document.addEventListener('eira:cart:open', function () {
    openDrawer();
  });

  /* Listen for cart:updated from bundle flow (and any other module that adds to cart) */
  document.addEventListener('cart:updated', function(e) {
    var cart = e.detail && e.detail.cart;
    if (cart) {
      updateDrawerFromCart(cart);
      openDrawer();
    } else {
      /* No cart data passed — fetch fresh */
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then(function(res) { return res.json(); })
        .then(function(freshCart) {
          updateDrawerFromCart(freshCart);
          openDrawer();
        });
    }
  });

  /* Shopify editor reload */
  document.addEventListener('shopify:section:load', function() {
    init();
  });
})();
